/**
 * Platform Email Settings — single global Zoho ZeptoMail (India) config.
 * Only platform_admin can edit. Used by every org as the default sender.
 */
import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { EnvelopeSimple, FloppyDisk, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PlatformEmailSettingsPage() {
    const { user } = useAuth();
    const [s, setS] = useState(null);
    const [form, setForm] = useState({ zepto_token: "", sender_email: "", sender_name: "" });
    const [busy, setBusy] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [testing, setTesting] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/platform/email-settings");
            setS(data);
            setForm({
                zepto_token: "",
                sender_email: data.sender_email || "",
                sender_name: data.sender_name || "FormFlo",
            });
            if (user?.email && !testTo) setTestTo(user.email);
        } catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const sendTest = async () => {
        if (!testTo) { toast.error("Pick an address to send the test to."); return; }
        setTesting(true);
        try {
            const { data } = await api.post("/platform/email-settings/test", { to: testTo });
            if (data?.status === "sent") toast.success(`Test email sent to ${testTo}`);
            else if (data?.status === "mocked") toast(`ZeptoMail not configured yet — email logged to server console (to=${testTo})`);
            else toast.error(data?.error || "Send failed — check server logs");
        } catch (e) { toast.error(extractError(e)); }
        finally { setTesting(false); }
    };

    const save = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const payload = {};
            if (form.zepto_token) payload.zepto_token = form.zepto_token;
            if (form.sender_email !== "") payload.sender_email = form.sender_email;
            if (form.sender_name !== "") payload.sender_name = form.sender_name;
            await api.put("/platform/email-settings", payload);
            toast.success("ZeptoMail settings saved");
            setForm({ ...form, zepto_token: "" });
            await load();
        } catch (err) { toast.error(extractError(err)); }
        finally { setBusy(false); }
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Platform"
                title="Email settings"
                description="Single global Zoho ZeptoMail configuration used by every organization for transactional emails."
                testid="platform-email-header"
            />

            <div className="brut-section p-8 max-w-2xl" data-testid="platform-email-card">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="inline-flex border border-border bg-secondary p-2 rounded-sm">
                            <EnvelopeSimple size={18} weight="duotone" className="text-primary" />
                        </div>
                        <div>
                            <div className="label-overline">Provider</div>
                            <h2 className="text-xl font-bold tracking-tight">Zoho ZeptoMail · India</h2>
                        </div>
                    </div>
                    {s && (s.configured ? <Badge className="rounded-sm bg-primary" data-testid="zepto-status-configured">Configured</Badge> : <Badge variant="outline" className="rounded-sm" data-testid="zepto-status-mock">Mock mode</Badge>)}
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                    When unconfigured, emails are logged to the server console (mock mode). Generate a
                    <span className="font-mono text-xs mx-1">Send Mail Token</span>
                    from your ZeptoMail dashboard → Mail Agents → Setup Info → API tab. Endpoint:
                    <span className="font-mono text-xs ml-1">api.zeptomail.in/v1.1/email</span>.
                </p>
                <form onSubmit={save} className="space-y-4">
                    <div>
                        <Label className="label-overline">ZeptoMail Send Mail Token</Label>
                        <Input
                            type="password" value={form.zepto_token}
                            onChange={(e) => setForm({ ...form, zepto_token: e.target.value })}
                            placeholder={s?.configured ? "•••••••• (leave blank to keep)" : "Zoho-enczapikey ..."}
                            className="mt-2 rounded-sm font-mono"
                            data-testid="zepto-token-input"
                        />
                        <p className="text-[10px] font-mono text-muted-foreground mt-2">Stored encrypted with the server-side key.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="label-overline">Sender email</Label>
                            <Input
                                type="email" value={form.sender_email}
                                onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                                placeholder="noreply@formflo.in"
                                className="mt-2 rounded-sm"
                                data-testid="zepto-sender-email-input"
                            />
                        </div>
                        <div>
                            <Label className="label-overline">Sender name</Label>
                            <Input
                                value={form.sender_name}
                                onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                                placeholder="FormFlo"
                                className="mt-2 rounded-sm"
                                data-testid="zepto-sender-name-input"
                            />
                        </div>
                    </div>
                    <Button type="submit" disabled={busy} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-platform-email-btn">
                        <FloppyDisk size={14} className="mr-1" /> {busy ? "Saving…" : "Save settings"}
                    </Button>
                </form>
            </div>

            <div className="brut-section p-8 max-w-2xl mt-6" data-testid="zepto-test-card">
                <div className="flex items-center gap-3 mb-3">
                    <div className="inline-flex border border-border bg-secondary p-2 rounded-sm">
                        <PaperPlaneTilt size={18} weight="duotone" className="text-primary" />
                    </div>
                    <div>
                        <div className="label-overline">Verify delivery</div>
                        <h3 className="text-lg font-bold tracking-tight">Send a test email</h3>
                    </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    Fires a one-shot email through your saved ZeptoMail settings. If the provider isn&apos;t configured yet, the message is logged to the server console instead.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[260px]">
                        <Label className="label-overline">Send to</Label>
                        <Input
                            type="email" value={testTo}
                            onChange={(e) => setTestTo(e.target.value)}
                            placeholder="you@example.com"
                            className="mt-2 rounded-sm"
                            data-testid="zepto-test-to-input"
                        />
                    </div>
                    <Button onClick={sendTest} disabled={testing || !testTo} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground h-10" data-testid="zepto-test-send-btn">
                        <PaperPlaneTilt size={14} className="mr-1" /> {testing ? "Sending…" : "Send test"}
                    </Button>
                </div>
            </div>
        </PageContainer>
    );
}
